"use client";

/**
 * HeroCoinChips — the floating "coin chips" beside the landing-hero sparrow.
 *
 * Sprint 15 / F3: the chips used to show abstract amounts ($84, −$210, +$1,400).
 * The +$1,400 read as a *gain*, which fights the audit's leak framing. We now
 * name concrete, US-recognizable everyday outflows (brand + amount) so the
 * hero reads "here are your real leaks, named" — small recurring drips that
 * ladder up to the headline annual loss. No brand logos (plain text only) to
 * avoid trademark/asset weight; the wrapper is decorative (aria-hidden on the
 * hero container in page.tsx).
 *
 * Sprint 16 / F36: expanded 3 → 6 chips, research-driven brand/category mix
 * (docs/research/f36-impulse-buy-chips.json). Netflix → Spotify and DoorDash →
 * Uber Eats (category variety + a fresh visual identity); Starbucks stays; new
 * Shein / Sephora / HelloFresh cover fast-fashion, beauty and unused-
 * subscription leaks. Laid out as a 2+2+2 wreath around the sparrow (top pair,
 * side pair, bottom pair) so the six chips frame the mascot without covering
 * its face. Reduced-motion users get static chips automatically — the
 * `.s5-coin-chip` bob is disabled in the `prefers-reduced-motion` block of
 * styles/globals.css; no per-chip JS needed.
 *
 * Sprint 17 / F36+: pool expanded 6 → 12. Every page-load picks a random 6
 * from the pool, stable within the session (sessionStorage key). Server render
 * shows the first 6 (SSR-safe fallback for no-JS); after mount the client
 * swaps in the session-seeded set. The 2+2+2 wreath positions are fixed — only
 * the chip content rotates.
 *
 * Sprint 18 / F40: brand icons re-integrated onto the 12-chip pool.
 * Each chip now carries an iconSlug that maps to chip-icons.tsx (simple-icons
 * MIT for 8 brands; custom abstract glyphs for Shein, Sephora, Temu, Ulta).
 *
 * Reuses the .s5-coin-chip class (styles/globals.css). Labels are longer than
 * the old bare amounts, so the font is dropped to 12px and each chip is width-
 * capped to keep it clear of the sparrow.
 */

import { useEffect, useState } from "react";

import { ChipIconSvg } from "./chip-icons";

interface Chip {
  /** Brand + amount label shown in the chip. */
  label: string;
  /** Spending category this leak represents (research taxonomy; not rendered). */
  category: string;
  /** Bob-animation delay shared between pool item and rendered position. */
  animationDelay?: string;
  /** Icon slug passed to ChipIconSvg — must match a key in CHIP_ICONS. */
  iconSlug: string;
}

/** Position descriptor for each of the 6 fixed 2+2+2 wreath slots. */
interface ChipPosition {
  /** Tailwind position + color utilities layered on .s5-coin-chip. */
  className: string;
  /** Bob-animation phase offset class (anim-delay-* from globals.css). */
  animDelayClass: string;
}

/**
 * 12-chip pool — 6 original (F36) + 6 new (F36+).
 * Category spread: streaming×2, food-delivery×2, coffee×1, fast-fashion×2,
 * beauty×2, unused-subscription×2, apps×1 (12 total).
 * pickChips() enforces max-1-per-category so no two same-category chips
 * appear together in the displayed 6.
 */
export const CHIP_POOL: Chip[] = [
  // ── Original 6 (F36) ──────────────────────────────────────────────────────
  { label: "$12.99 — Spotify", category: "streaming", iconSlug: "spotify" },
  { label: "$68 — Sephora", category: "beauty", iconSlug: "sephora" },
  { label: "$5.45 — Starbucks", category: "coffee", iconSlug: "starbucks" },
  { label: "$27.50 — Uber Eats", category: "food delivery", iconSlug: "uber-eats" },
  { label: "$58 — Shein", category: "fast fashion", iconSlug: "shein" },
  { label: "$59.95 — HelloFresh", category: "unused subscription", iconSlug: "hellofresh" },
  // ── New 6 (F36+/F40) ──────────────────────────────────────────────────────
  { label: "$22.99 — Netflix", category: "streaming", iconSlug: "netflix" },
  { label: "$24 — DoorDash", category: "food delivery", iconSlug: "doordash" },
  { label: "$42 — Temu", category: "fast fashion", iconSlug: "temu" },
  { label: "$54 — Ulta", category: "beauty", iconSlug: "ulta" },
  { label: "$12.99 — Peloton App", category: "unused subscription", iconSlug: "peloton" },
  { label: "$7 — Duolingo Super", category: "apps", iconSlug: "duolingo" },
];

/**
 * Fixed 2+2+2 wreath positions — top pair / side pair / bottom pair.
 *
 * Sprint 19 / L1: bottom pair raised from top-[76%]/top-[78%] → top-[60%]/top-[62%].
 * The SparrowHeroBubble sits at -bottom-2 / md:-bottom-4 relative to the
 * mascot container, which means anything below ~65% of the square overlaps the
 * bubble on small viewports (375–768 px). Raising to 60/62% gives a clear
 * ~80 px gap between the chip bottom edge and the bubble top at 375 px.
 */
const POSITIONS: ChipPosition[] = [
  // Top pair
  { className: "left-[-6%] top-[4%] text-s5-warm", animDelayClass: "anim-delay-0" },
  { className: "right-[-3%] top-[6%] text-s5-loss", animDelayClass: "anim-delay--0_8" },
  // Side pair
  { className: "left-[-9%] top-[42%] text-s5-warm", animDelayClass: "anim-delay--1_6" },
  { className: "right-[-8%] top-[40%] text-s5-warm", animDelayClass: "anim-delay--2_3" },
  // Bottom pair — raised (L1, Sprint 19) to clear the SparrowHeroBubble
  { className: "left-[-4%] top-[60%] text-s5-loss", animDelayClass: "anim-delay--3" },
  { className: "right-[-5%] top-[62%] text-s5-loss", animDelayClass: "anim-delay--3_7" },
];

const SESSION_KEY = "hcc_seed_v1";

/**
 * Deterministic seeded shuffle (Mulberry32 PRNG) — same seed → same order.
 * Returns a new array; does not mutate the input.
 */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  // Mulberry32: fast, good enough for UI randomness, no crypto needed.
  let s = seed >>> 0;
  const rand = () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) >>> 0;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return out;
}

/**
 * Pick 6 chips from the 12-chip pool using a numeric seed.
 *
 * Strategy:
 * 1. Shuffle the pool with the seed.
 * 2. Greedily pick chips ensuring max 1 per category (category-diversity pass).
 * 3. If fewer than 6 unique-category chips remain, fill from the leftover
 *    (allows category repeat only as last resort).
 *
 * This means the selected 6 cover as many different categories as possible,
 * never showing e.g. two streaming chips at once if avoidable.
 */
export function pickChips(seed: number): Chip[] {
  const shuffled = seededShuffle(CHIP_POOL, seed);
  const picked: Chip[] = [];
  const usedCategories = new Set<string>();

  // First pass: one chip per category.
  for (const chip of shuffled) {
    if (picked.length >= 6) break;
    if (!usedCategories.has(chip.category)) {
      picked.push(chip);
      usedCategories.add(chip.category);
    }
  }

  // Second pass: fill remaining slots with any leftover chips.
  if (picked.length < 6) {
    for (const chip of shuffled) {
      if (picked.length >= 6) break;
      if (!picked.includes(chip)) {
        picked.push(chip);
      }
    }
  }

  return picked;
}

/** Parse or create the session seed. Runs only on the client. */
function getOrCreateSeed(): number {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) return parsed;
    }
    const fresh = Math.floor(Math.random() * 0xffffffff);
    sessionStorage.setItem(SESSION_KEY, String(fresh));
    return fresh;
  } catch {
    // sessionStorage blocked (e.g. private-browsing strict mode) — use a
    // one-off random so the page still works; seed won't persist across reloads.
    return Math.floor(Math.random() * 0xffffffff);
  }
}

/** SSR fallback: the first 6 chips of the pool (stable, deterministic). */
const SSR_CHIPS = CHIP_POOL.slice(0, 6);

export function HeroCoinChips() {
  // Server render: use the first 6 chips (no sessionStorage available).
  // After mount: swap to the session-seeded selection.
  const [chips, setChips] = useState<Chip[]>(SSR_CHIPS);

  useEffect(() => {
    const seed = getOrCreateSeed();
    setChips(pickChips(seed));
  }, []);

  return (
    <>
      {chips.map((chip, i) => {
        const pos = POSITIONS[i]!;
        return (
          <span
            key={chip.label}
            className={`s5-coin-chip inline-flex items-center gap-1 max-w-[58%] text-[12px] ${pos.className} ${pos.animDelayClass}`}
          >
            <ChipIconSvg slug={chip.iconSlug} size={14} />
            {chip.label}
          </span>
        );
      })}
    </>
  );
}
